import { useMutation } from "@tanstack/react-query";

import { payoffApi } from "../../../api/client";

export type LetterKind = "payoff" | "settlement";

export interface FetchLetterContentInput {
  kind: LetterKind;
  appId: string;
  documentId: string;
  popup: Window | null;
}

/**
 * Fetches a generated letter's PDF content and points the already-open
 * placeholder tab (`popup`, opened synchronously on click to dodge popup
 * blockers) at it. Throws when the popup is missing or was closed before the
 * fetch resolved, rather than silently doing nothing.
 * @param {FetchLetterContentInput} input - The letter kind, its identifiers, and the target popup window.
 * @returns {Promise<void>} Resolves once the popup's location is set.
 */
async function fetchLetterContentAndOpen({ kind, appId, documentId, popup }: FetchLetterContentInput): Promise<void> {
  if (!popup || popup.closed) {
    throw new Error("The new tab was closed or blocked before the letter finished loading.");
  }

  const response =
    kind === "payoff"
      ? await payoffApi.getPayoffLetterContent(appId, documentId, { responseType: "arraybuffer" })
      : await payoffApi.getSettlementLetterContent(appId, documentId, { responseType: "arraybuffer" });

  if (popup.closed) {
    throw new Error("The new tab was closed before the letter finished loading.");
  }

  const contentType = (response.headers?.["content-type"] as string) || "application/pdf";
  const blob = new Blob([response.data], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  popup.location.href = blobUrl;

  // Revoking on the popup's own `unload` (rather than immediately) would fire
  // as soon as `location.href` navigates it away from `about:blank`, freeing
  // the blob before it loads. Wait for the blob document itself to load, then
  // arm the revoke for whenever the popup later closes or navigates further.
  popup.addEventListener?.(
    "load",
    () => {
      popup.addEventListener?.("unload", () => URL.revokeObjectURL(blobUrl), { once: true });
    },
    { once: true }
  );
}

/**
 * Fetches a generated payoff/settlement letter's PDF content and opens it in
 * the popup window passed to `mutate`. On failure, closes that popup so the
 * analyst isn't left with a permanent blank tab.
 * @returns {ReturnType<typeof useMutation<void, unknown, FetchLetterContentInput>>} The mutation.
 */
export function useFetchLetterContent() {
  return useMutation({
    mutationFn: fetchLetterContentAndOpen,
    onError: (_error, variables) => {
      variables.popup?.close();
    },
  });
}
